<script lang="ts">
  /**
   * Feuille de labels — l'action de l'appui long sur un fil.
   *
   * Ne montre que les labels que l'utilisateur possède : les labels système
   * (INBOX, SENT, UNREAD…) sont manipulés par les autres actions, et les
   * labels préfixés `[Outil]/…` appartiennent à d'autres applications.
   */

  import { gmailState } from "../gmail/store.svelte";
  import { labelsState, displayLabel } from "../gmail/labels.svelte";
  import { toggleThreadLabel } from "../gmail/sync";
  import { labelSheetState, closeLabelSheet } from "./labelSheetStore.svelte";
  import { haptic } from "../gestures/haptic";
  import { dismissSheet } from "../gestures/dismissSheet";
  import { lockBodyScroll, unlockBodyScroll } from "../gestures/bodyLock";
  import Icon from "./shared/Icon.svelte";

  $effect(() => {
    if (labelSheetState.threadId) {
      lockBodyScroll();
      return () => unlockBodyScroll();
    }
  });

  const thread = $derived.by(() => {
    const id = labelSheetState.threadId;
    if (!id) return null;
    if (gmailState.currentThread?.id === id) return gmailState.currentThread;
    return gmailState.rows.find((r) => r.thread.id === id)?.thread ?? null;
  });

  /** Les labels posables : ceux de l'utilisateur, hors préfixes d'outils. */
  const labels = $derived(
    labelsState.all
      .filter((l) => l.type !== "system" && !/^\[[^\]]+\]/.test(l.name))
      .sort((a, b) => a.name.localeCompare(b.name, "fr")),
  );

  /** Un label est posé si TOUS les messages du fil le portent. */
  function isOn(labelId: string): boolean {
    const t = thread;
    if (!t || t.messages.length === 0) return false;
    return t.messages.every((m) => (m.labelIds ?? []).includes(labelId));
  }

  function toggle(labelId: string) {
    const id = labelSheetState.threadId;
    if (!id) return;
    haptic("medium");
    void toggleThreadLabel(id, labelId, !isOn(labelId));
  }
</script>

{#if labelSheetState.threadId}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="scrim" onclick={closeLabelSheet}></div>
  <div class="sheet" use:dismissSheet={{ onClose: closeLabelSheet }}>
    <div class="grip" aria-hidden="true"></div>
    <div class="title-row">
      <h2>Labels</h2>
      <button class="close" onclick={closeLabelSheet} aria-label="Fermer">
        <Icon name="close" size={14} />
      </button>
    </div>

    {#if labels.length === 0}
      <p class="empty">Aucun label personnel.</p>
    {:else}
      <ul>
        {#each labels as l (l.id)}
          {@const on = isOn(l.id)}
          <li>
            <button class="row" class:on onclick={() => toggle(l.id)}>
              <span class="box" class:checked={on}>
                {#if on}<Icon name="check" size={9} />{/if}
              </span>
              <span class="name">{displayLabel(l.name)}</span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    z-index: 60;
    background: rgba(0, 0, 0, 0.35);
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 61;
    max-height: 70vh;
    overflow-y: auto;
    background: var(--bg);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    box-shadow: var(--shadow-pop);
    padding: 8px 16px calc(env(safe-area-inset-bottom) + 16px);
    animation: mfx-sheet 260ms var(--ease-ios);
  }
  .grip {
    width: 36px;
    height: 4px;
    margin: 0 auto 10px;
    border-radius: 2px;
    background: var(--border);
  }
  .title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
  }
  h2 {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 22px;
    line-height: 1;
  }
  .close {
    width: 28px;
    height: 28px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: var(--bg-hover);
    color: var(--fg-muted);
  }

  ul { list-style: none; margin: 0; padding: 0; }
  .row {
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    min-height: 44px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-subtle);
    text-align: left;
  }
  .box {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border: 1px solid var(--border);
    border-radius: var(--radius-xs);
    color: var(--accent-fg);
  }
  .box.checked {
    background: var(--accent);
    border-color: var(--accent);
  }
  .name { font-size: 14px; color: var(--fg); }
  .row.on .name { font-weight: 550; }

  .empty {
    padding: 2rem 0;
    text-align: center;
    font-family: var(--font-serif);
    font-style: italic;
    font-size: 18px;
    color: var(--fg-subtle);
  }
</style>
